import { ImportParams, PrimariaApi } from "@uxland/primary-shell";
import { LitElement, css, html, unsafeCSS } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { pluginName } from "../../constants";
import { lazyInject } from "../../infrastructure/ioc/container";
import { TYPES } from "../../infrastructure/ioc/types";
import { connectedProperty } from "../../infrastructure/state/connected-property";
import { IActivityHistoryGroup, IActivityHistoryItem, IHistoryDataImporterContext } from "../domain/model";
import { VirtualScrollerState } from "../list/UI/timeline/virtual-scroller";
import { SearchActivityHistoryItems } from "../search/request";
import { activityHistorySearchStringSelector } from "../search/selectors";
import { activityHistoryGroupedItems, historyDataImporterRulesByCollectionIdSelector } from "../selectors";
import { prepareGroupComponents } from "../shared/prepare-group-components";
import { INITIAL_SCROLLER_STATE, ScrollerController } from "../shared/scroller-controller";
import { ImporterViewFilters, RulesByCollectionId, applyImporterFilters, flattenGroupItems } from "./filters";
import styles from "./styles.css?inline";
import { template } from "./template";

export type TristateChecked = "all" | "some" | "none";

type AvailableDiagnostic = { code: string; catalog: string; description: string };

/** Escapes a plain-text string so it can be safely embedded as HTML text. */
const escapeHtml = (value: string): string => {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
};

const ALLOWED_HTML_TAGS = ["p", "br", "span", "div", "strong", "em", "u", "s", "sub", "sup", "b", "i", "a", "code", "pre", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li"];
const strayLessThan = new RegExp(`<(?!/?(?:${ALLOWED_HTML_TAGS.join("|")})\\b)`, "gi");
/** Escapes `<` that don't open a known HTML tag, so strings like `(<P50)` aren't parsed as tags. */
const escapeUnknownTags = (html: string): string => html.replace(strayLessThan, "&lt;");

/**
 * Builds the `{ raw, html }` text for a selection from each item's `content`.
 * Items with only a plain-text body are wrapped in a <p>; items without any
 * body contribute nothing. Parts are joined the same way the shell aggregates
 * text across plugins (blank line for raw, adjacent blocks for html).
 */
const buildSelectionText = (items: IActivityHistoryItem[]): { raw: string; html: string } => {
  const rawParts: string[] = [];
  const htmlParts: string[] = [];
  for (const item of items) {
    const plain = item.content?.plainText?.trim();
    const itemHtml = item.content?.html?.trim();
    if (plain) rawParts.push(plain);
    if (itemHtml) htmlParts.push(escapeUnknownTags(itemHtml));
    else if (plain) htmlParts.push(`<p>${escapeHtml(plain)}</p>`);
  }
  return { raw: rawParts.join("\n\n"), html: htmlParts.join("") };
};

@customElement("activity-history-importer")
export class ActivityHistoryImporter extends LitElement {
  static styles = css`
    ${unsafeCSS(styles)}
  `;

  render() {
    return html`${template(this)}`;
  }

  // ── API & store ─────────────────────────────────────────────────────────
  @lazyInject(TYPES.primaryApi)
  api: PrimariaApi;

  @connectedProperty(activityHistoryGroupedItems)
  historyGroups: IActivityHistoryGroup[] = [];

  @connectedProperty(activityHistorySearchStringSelector)
  searchString = "";

  @connectedProperty(historyDataImporterRulesByCollectionIdSelector)
  rulesByCollectionId: RulesByCollectionId = {};

  // ── Filters (local) ─────────────────────────────────────────────────────
  @state() todayOnly = false;
  @state() selectedDiagnosticCodes: string[] = [];
  @state() availableDiagnostics: AvailableDiagnostic[] = [];

  // ── Derived state ───────────────────────────────────────────────────────
  @state() filteredGroups: IActivityHistoryGroup[] = [];
  @state() totalCount = 0;

  // ── Selection ───────────────────────────────────────────────────────────
  @state() selectedIds: string[] = [];

  // ── Virtual scroller ────────────────────────────────────────────────────
  @query(".virtual-viewport") private _viewport!: HTMLElement;
  @state() _virtualScrollerState: VirtualScrollerState = INITIAL_SCROLLER_STATE;
  private _scrollerController = new ScrollerController<IActivityHistoryGroup>();

  // ── Misc internals ──────────────────────────────────────────────────────
  private _searchTimeout: number | null = null;
  private _didInitialPreselect = false;

  // ── Lifecycle ───────────────────────────────────────────────────────────
  connectedCallback() {
    super.connectedCallback();
    this.selectedIds = [];
    this._didInitialPreselect = false;
    const params = this.api?.importDataManager?.getCurrentImportParams?.() as ImportParams | undefined;
    this.availableDiagnostics = params?.diagnostics ?? [];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._scrollerController.teardown();
    this._clearSearchTimeout();
    this.api.broker.send(new SearchActivityHistoryItems(""));
  }

  updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    if (
      changedProperties.has("historyGroups") ||
      changedProperties.has("todayOnly") ||
      changedProperties.has("selectedDiagnosticCodes")
    ) {
      void this._recomputeFiltered();
    }

    if (!this._didInitialPreselect && this.historyGroups?.length) {
      this._didInitialPreselect = true;
      this._preselectMatchingItems();
    }

    if (changedProperties.has("filteredGroups")) {
      this._syncVirtualScroller();
    }

    if (changedProperties.has("selectedIds")) {
      this._publishSelection();
    }
  }

  // ── Event handlers ──────────────────────────────────────────────────────
  _onSearchChange(event: CustomEvent) {
    if (typeof event.detail !== "string") return;
    this._clearSearchTimeout();
    this._searchTimeout = window.setTimeout(() => {
      this.api.broker.send(new SearchActivityHistoryItems(event.detail));
    }, 300);
  }

  _onToggleToday(event: Event) {
    this.todayOnly = (event.target as HTMLInputElement).checked;
  }

  _onDiagnosticsChanged(event: CustomEvent) {
    const detail = event.detail as { selectedItems?: { value: string }[] } | undefined;
    this.selectedDiagnosticCodes = (detail?.selectedItems || []).map((s) => s.value);
  }

  _toggleItem(itemId: string) {
    this.selectedIds = this.selectedIds.includes(itemId)
      ? this.selectedIds.filter((id) => id !== itemId)
      : [...this.selectedIds, itemId];
  }

  _toggleVisit(group: IActivityHistoryGroup, selected: boolean) {
    this._setSelection(
      flattenGroupItems([group]).map((it) => it.id),
      selected,
    );
  }

  _toggleSubGroup(items: { id: string }[], selected: boolean) {
    this._setSelection(
      items.map((it) => it.id),
      selected,
    );
  }

  _toggleAll(event: Event) {
    this._setSelection(
      flattenGroupItems(this.filteredGroups).map((it) => it.id),
      (event.target as HTMLInputElement).checked,
    );
  }

  // ── Template helpers ────────────────────────────────────────────────────
  isItemSelected(itemId: string): boolean {
    return this.selectedIds.includes(itemId);
  }

  visitState(group: IActivityHistoryGroup): TristateChecked {
    return this._tristate(flattenGroupItems([group]).map((it) => it.id));
  }

  subGroupState(items: { id: string }[]): TristateChecked {
    return this._tristate(items.map((it) => it.id));
  }

  allState(): TristateChecked {
    return this._tristate(flattenGroupItems(this.filteredGroups).map((it) => it.id));
  }

  hasDiagnosticsFilter(): boolean {
    return this.availableDiagnostics.length > 0;
  }

  diagnosticsOptions() {
    return this.availableDiagnostics.map((d) => ({ label: d.description, value: d.code }));
  }

  /**
   * Maps virtual scroller indices to actual filtered groups for rendering.
   *
   * The virtual scroller tells us which item positions are visible (e.g., items 5-7).
   * This getter translates those position numbers into the actual group objects
   * from filteredGroups that need to be displayed.
   * @example
   * virtualItems = [{ index: 0 }, { index: 1 }, { index: 2 }, { index: 3 }]  // 4 items visible at positions 0, 1, 2, 3
   * returns [filteredGroups[0], [filteredGroups[1], [filteredGroups[2], [filteredGroups[3], ...] // the actual groups to render
   *
   * @returns The activity history groups currently visible in the viewport
   */
  get _visibleRows(): { index: number; group: IActivityHistoryGroup }[] {
    return this._virtualScrollerState.virtualItems
      .map((vi) => ({ index: vi.index, group: this.filteredGroups?.[vi.index] }))
      .filter((row): row is { index: number; group: IActivityHistoryGroup } => Boolean(row.group));
  }

  /** Callback ref used by each `.visit` so the virtualizer measures its real
   * height (variable-height visits) instead of relying on the estimate. */
  _measureElement = (el: Element | undefined) => {
    if (el) this._scrollerController.measureElement(el as HTMLElement);
  };
  /**
   * Calculates the vertical offset (in pixels) to position the visible items.
   *
   * When scrolling, visible items are positioned using CSS `transform: translateY()`.
   * This getter returns how many pixels to shift the container down so the visible
   * items appear in the correct scroll position.
   *
   * @example
   * First visible item is at pixel 5000 → returns 5000
   * CSS applies: transform: translateY(5000px)
   *
   * @returns Vertical offset in pixels for the items container
   */
  get _containerOffset(): number {
    return this._virtualScrollerState.virtualItems[0]?.start ?? 0;
  }

  get _totalHeight(): number {
    return this._virtualScrollerState.totalHeight;
  }

  // ── Private helpers ─────────────────────────────────────────────────────
  private _clearSearchTimeout() {
    if (this._searchTimeout !== null) {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = null;
    }
  }

  private _setSelection(ids: string[], selected: boolean) {
    if (selected) {
      const additions = ids.filter((id) => !this.selectedIds.includes(id));
      if (additions.length) this.selectedIds = [...this.selectedIds, ...additions];
    } else {
      const toRemove = new Set(ids);
      this.selectedIds = this.selectedIds.filter((id) => !toRemove.has(id));
    }
  }

  private _tristate(ids: string[]): TristateChecked {
    if (!ids.length) return "none";
    const count = ids.filter((id) => this.selectedIds.includes(id)).length;
    if (count === 0) return "none";
    if (count === ids.length) return "all";
    return "some";
  }

  private async _recomputeFiltered() {
    const filters: ImporterViewFilters = {
      todayOnly: this.todayOnly,
      selectedDiagnosticCodes: this.selectedDiagnosticCodes,
    };
    const raw = applyImporterFilters(this.historyGroups || [], filters, this.rulesByCollectionId);
    this.filteredGroups = await prepareGroupComponents(raw);
    this.totalCount = flattenGroupItems(this.filteredGroups).length;
  }

  private _syncVirtualScroller() {
    this._virtualScrollerState = this._scrollerController.sync({
      viewport: this._viewport,
      items: this.filteredGroups,
      estimateItemHeight: () => 200,
      onStateChange: (state) => {
        this._virtualScrollerState = state;
        this.requestUpdate();
      },
    });
  }

  /**
   * On the first time the modal receives history data, auto-selects every
   * item whose collection's rule (`shouldPreselect`) returns true. The
   * activity-history plugin stays generic — each plugin owns its own
   * preselect predicate.
   */
  private _preselectMatchingItems() {
    const ctx: IHistoryDataImporterContext = { diagnostics: this.availableDiagnostics };
    const matchingIds: string[] = [];

    const collect = (items: IActivityHistoryItem[]) => {
      for (const item of items) {
        const rule = item.type ? this.rulesByCollectionId[item.type] : undefined;
        if (!rule?.shouldPreselect) continue;
        if (rule.shouldPreselect(item, ctx)) matchingIds.push(item.id);
      }
    };

    for (const group of this.historyGroups || []) {
      collect(group.items || []);
      for (const sub of group.subGroups || []) collect(sub.items);
    }

    if (matchingIds.length) {
      this.selectedIds = [...new Set([...this.selectedIds, ...matchingIds])];
    }
  }

  private _publishSelection() {
    const selectedSet = new Set(this.selectedIds);
    const groups = (this.historyGroups || [])
      .map((group) => ({
        ...group,
        items: (group.items || []).filter((it) => selectedSet.has(it.id)),
        subGroups: (group.subGroups || [])
          .map((sub) => ({ ...sub, items: sub.items.filter((it) => selectedSet.has(it.id)) }))
          .filter((sub) => sub.items.length > 0),
      }))
      .filter((g) => g.items.length > 0 || g.subGroups.length > 0);

    // Publish both the structured selection (for consumers that need the raw
    // items) and the formatted text built from each item's `content`, so the
    // destination plugin can just append `result.text` without re-deriving it.
    this.api.importDataManager.selectItems({
      pluginId: pluginName,
      data: groups as any[],
      text: buildSelectionText(flattenGroupItems(groups as IActivityHistoryGroup[])),
    });
  }
}
